<template>
  <div class="login register container-fluid">
    <div class="row eq-height">
      <div class="col-lg-7 login-left">
        <div class="row mt-20 side-pad login-header">
          <div class="col-6">
            <router-link to="/">
              <img
                class="login__image"
                src="@/assets/img/bettermynd-logo@2x.png"
                alt="BetterMynd"
              />
            </router-link>
          </div>
        </div>
        <div class="login-content text-center">
          
          <loader v-if="loading" />

          <card v-else page="login">
            <div class="registration-header">
              <h1> Link to Campus </h1>
            </div>
            <form-component>
              <form novalidate="novalidate" @submit.prevent="register">
                <div class="form-row">
                  <div class="col-12">
                    <div class="selected-email">
                      <font-awesome-icon
                        :icon="['fas', 'check']"
                        class="icon alt"
                      />
                      {{ email }}
                    </div>
                  </div>
                  <div class="col-12 registration-intro mb-2">
                    <p>
                      Welcome to BetterMynd! 
                      Let's start with the basics. 
                      Select your campus below.
                    </p>
                  </div>
                  <campus-auto-complete-dropdown 
                    v-model="campus"
                    :emailDomain="userEmailDomain"
                  />
                  <div class="row" v-if="campus && !validEmail">
                    <div class="col-12" style="color: red;">
                      <font-awesome-icon
                        :icon="['fas', 'times']"
                        class="icon alt"
                      />
                      This campus requires an email address from the following 
                      {{campus.domains.length === 1 ? 'domain' : 'domains'}}: 
                      <ul>
                        <li v-for="domain in campus.domains" :key="domain">
                          @{{domain}}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div class="row" v-if="campus && !validEmail">
                    <div class="col-12">
                      <p>
                        <font-awesome-icon
                          :icon="['fas', 'exclamation-circle']"
                          class="icon alt"
                        />
                        Registered using your personal email? 
                        <span class="click-here" @click="logoutAndRouteToRegisterPage">
                          Click here 
                        </span>
                        to go back and register using your campus email.
                      </p>
                    </div>
                  </div>

                </div>

                <div class="row mt-30">
                  <div class="col-12 col-md-6">
                    <p>
                      Can't find your school? 
                      <span class="click-here" @click="requestSchool()">
                        Click here 
                      </span>
                      to let us know!
                    </p>
                  </div>
                  <div class="col-12 col-md-6">
                    <button
                      class="btn btn--primary mt-30"
                      :disabled="loading"
                      type="submit"
                    >
                      <font-awesome-icon
                        v-if="loading"
                        :spin="true"
                        :icon="['fas', 'spinner']"
                        class="icon alt"
                      />
                      <span v-else>Continue</span>
                    </button>
                  </div>
                </div>
              </form>
            </form-component>
          </card>
        </div>
      </div>
      <div class="d-none d-lg-block col-lg-5 register-right">
        <div class="vertical-align-md">
          <img
            class="forgot__image"
            src="@/assets/img/register-img@2x.png"
            alt="BetterMynd"
          />
        </div>
      </div>
    </div>
    <request-school-modal ref="requestSchoolModal" />
  </div>
</template>

<script>
import UserClient from '@/api/UserClient';
import Card from '@/components/Card.vue';
import FormComponent from '@/components/FormComponent.vue';
import RequestSchoolModal from '@/components/modals/RequestSchoolModal.vue';
import CampusAutoCompleteDropdown from './components/CampusAutoCompleteDropdown.vue'
import Loader from '@/components/Loader.vue';

export default {
  name: 'LinkCampus',

  components: {
    Card,
    FormComponent,
    RequestSchoolModal,
    Loader,
    CampusAutoCompleteDropdown,
  },

  data() {
    return {
      userClient: new UserClient(this.$http),

      loading: false,

      firstName: this.$globals.keycloak.tokenParsed.given_name || '',
      lastName: this.$globals.keycloak.tokenParsed.family_name || '',
      email: this.$globals.keycloak.tokenParsed.email,
      campus: null,
    };
  },

  async mounted() {
    try {
      this.loading = true;

      await this.userClient.autoRegisterAsStudent();
      await this.initializeNewlyRegisteredUser();
      await this.goToUserHome();
    } catch(ex) {
      console.error(ex);
    } finally { 
      this.loading = false;
    }
  },

  computed: {
    validEmail() {
        return this.campus 
            && this.campus.domains.some(domain => this.email.indexOf(`@${domain}`) > -1);
    },
    userEmailDomain() {
      return this.email.substring(this.email.indexOf("@")+1);
    },
    domainsStr() {
      if(!this.campus)
        return [];
      return this.campus.domains.reduce((prev, curr, i) => prev + `${i > 0 ? ", " : ""}@${curr}`, '');
    }
  },

  methods: {
    logoutAndRouteToRegisterPage() {
      this.$globals.keycloak.logout({ redirectUri: window.location.origin });
      this.$globals.keycloak.register({ redirectUri: window.location.origin });
    },

    requestSchool() {
      this.$refs.requestSchoolModal.show();
    },

    async register() {
      if(!this.campus) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Please select a campus.',
          error: true
        });
        return;
      }
      if(!this.validEmail) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Email does not match campus requirements!',
          error: true
        });
        return;
      }
      
      this.loading = true;
      try {
        await this.userClient.registerStudent(
          this.firstName,
          this.lastName,
          this.campus.id
        );
        await this.initializeNewlyRegisteredUser();
        await this.goToUserHome();
      } catch (ex) {
        this.$store.commit('setGlobalAlert', {
          msg:
            ex.body?.error?.extra?.detail ||
            ex.body?.error?.message ||
            'Failed to link to campus. Please try again!',
          error: true
        });
      } finally {
        this.loading = false;
      }
    },
    async initializeNewlyRegisteredUser() {

      await this.userClient.syncIdentity();
      const user = await this.userClient.getRemoteUser();

      this.$store.commit('setUser', user);

      this.$globals.getUnreadCounts(user);

      this.$gtag.event('sign_up');
      this.$gtag.set('user_properties', {
        user_id: user.id.toString(),
        user_type: user.userTypeDisplay
      });
      
      if(window.clarity){
        window.clarity('identify', user.id.toString());
        window.clarity('set', 'user_id', user.id.toString());
        window.clarity('set', 'user_type', user.userTypeDisplay);
        window.clarity('set', 'bettermynd_version', window.betterMynd.version);
      }
    },
    goToUserHome() {
      if(!this.$store.getters.currentUser)
        throw new Error("No user has been initialized!");

      return this.$router.push({ 
        name: this.$store.getters.currentUser.homeRouteName 
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.click-here {
  color: $green;
  cursor: pointer;
}
.click-here:hover {
  text-decoration: underline;
}
form {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.form-row {
  flex-direction: column;
}
h1 {
  text-transform: capitalize;
  margin: 0.25rem 0 0.25rem;
  font-size: 2.25rem;
}

.hide {
  display: none;
}

.selected-email {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  border-radius: 7px;
  color: $green;
  font-weight: 500;
  font-size: 16px;
  word-break: break-all;
  line-height: 1.1;

  svg {
    margin-right: 4px;
  }
}

.registration-header {
  margin: 1rem 0 2rem;
}

.user-type-toggle {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  font-size: 16px;
  padding: 0;
  margin: 2px 0;
  text-align: left;
  line-height: 1.1;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  &:hover,
  &:focus {
    text-decoration: underline;
    color: $green;

    &:active {
      color: #fff;
    }
  }
}

.login .login__card {
  max-width: 625px;
  margin-top: 2rem;
}

.login.register .form-check label {
  font-size: 1rem;
}

.register {
  p {
    color: $white;
  }

  .login-left,
  .login-right,
  .login-content,
  .register-right {
    @media (min-width: $md-min) {
      margin-top: 0;
      margin-bottom: 0;
      min-height: 800px;
    }
  }

  .login-left {
    height: auto;
  }

  .form__error {
    color: $red !important;
  }

  .register-right {
    text-align: center;
    padding-left: 0;
    padding-right: 0;
    overflow: hidden;

    @media (min-width: $lg-min) {
      text-align: left;
      height: auto;
      min-height: 800px;
      background: rgb(42, 122, 161);
      background: linear-gradient(
        180deg,
        rgba(42, 122, 161, 1) 0%,
        rgba(40, 102, 159, 1) 100%
      );
    }

    img {
      width: 100%;
      height: auto;
      position: relative;
      z-index: 2;
      left: 0;

      @media (max-width: $md-max) {
        left: auto;
        right: auto;
        width: 75%;
        margin: 0 auto;
      }
    }
  }

  .top-label {
    color: $green;
    font-size: 12px;
    letter-spacing: 0.1rem;
  }

  a.green {
    color: $green;
  }

  .request-school-btn {
    background: none;
    color: white;
    border: none;
    padding: 0;
    box-shadow: none;
    margin-bottom: 20px;
    opacity: 0.6;

    &:hover {
      color: $green;
      opacity: 1;
    }
  }
}
</style>
